import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "492",
  "slug": "what-is-a-rift-valley",
  "title": "What Is a Rift Valley? How the Earth's Crust Splits Apart",
  "excerpt": "Rift valleys form where the planet is slowly tearing itself apart. Here is how they form, where to find them, and why one of them is splitting Africa in two.",
  "date": "2026-06-22",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1636636985438-4154b379aac5?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxyaWZ0JTIwdmFsbGV5JTIwbGFuZHNjYXBlfGVufDF8MHx8fDE3ODIxMTYwMzd8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "A rift valley is a long, low trough that forms where tectonic forces stretch and crack the crust, dropping a block of land between two roughly parallel faults. The East African Rift is the best-known example, and it may one day open into a new ocean.",
  "content": [
    {
      "type": "paragraph",
      "text": "Some landforms are built by piling material up, and others by tearing it apart. A rift valley belongs firmly to the second group. It is one of the few places on Earth where you can stand inside a crack in a continent that is, very slowly, pulling itself in two. From the air or from orbit, the largest rifts trace unmistakable straight-walled corridors across the land, often dotted with long, narrow lakes."
    },
    {
      "type": "heading",
      "text": "What Is a Rift Valley?"
    },
    {
      "type": "paragraph",
      "text": "A rift valley is a low, elongated lowland bounded by roughly parallel faults, created when the Earth’s crust is stretched until it fractures and a central block drops down. Geologists call that down-dropped block a graben, and the higher blocks left standing on either side are horsts. The result is a flat-floored valley with steep fault scarps for walls, sometimes hundreds of kilometres long but only tens of kilometres wide."
    },
    {
      "type": "paragraph",
      "text": "Rifts are different from valleys carved by rivers or glaciers. A river valley is cut downward by flowing water over time; a rift valley is pulled open from below by the movement of tectonic plates. That is why rift floors are often unusually flat and why their walls run in long, clean lines rather than meandering."
    },
    {
      "type": "heading",
      "text": "How Rift Valleys Form"
    },
    {
      "type": "paragraph",
      "text": "Rifting begins where the crust is being stretched, usually because of forces deep in the mantle or because two plates are drifting apart. As the crust thins, it cannot stretch indefinitely, so it cracks along faults. Blocks of rock slip downward along these faults, and the surface subsides into a trough."
    },
    {
      "type": "list",
      "items": [
        "Stretching: heat and movement in the mantle pull a section of crust apart, thinning it like soft toffee.",
        "Faulting: the brittle upper crust snaps along steep, parallel normal faults.",
        "Subsidence: the central block sinks between the faults, forming the graben that becomes the valley floor.",
        "Volcanism: thinned crust lets magma rise easily, so active rifts are often lined with volcanoes and hot springs."
      ]
    },
    {
      "type": "paragraph",
      "text": "Because the crust is so thin beneath a rift, magma reaches the surface more easily than almost anywhere else on land. Many of East Africa’s great volcanoes, including Kilimanjaro and Mount Kenya, sit along or near the rift system, and steam vents and geothermal fields are common across the valley floor."
    },
    {
      "type": "heading",
      "text": "From Rift Valley to New Ocean"
    },
    {
      "type": "paragraph",
      "text": "Given enough time, a continental rift can keep widening until the continent splits completely and the sea floods in. When that happens, the rift stops being a valley and becomes a young ocean basin. The Red Sea is the textbook example: it began as a rift between Africa and Arabia and is now a narrow sea that is still slowly widening as new ocean crust forms along its centre."
    },
    {
      "type": "paragraph",
      "text": "The East African Rift is widely seen as the next candidate. Stretching for thousands of kilometres from the Afar region down through Kenya, Tanzania, and Malawi, it is gradually pulling the Somali Plate away from the rest of Africa. The process is glacially slow by human standards, but over millions of years it could open a new ocean and turn the Horn of Africa into a separate landmass."
    },
    {
      "type": "heading",
      "text": "Rift Valleys Around the World"
    },
    {
      "type": "paragraph",
      "text": "Rifts are scattered across every continent, some still active and others long extinct and buried under younger rock. A few of the most striking:"
    },
    {
      "type": "list",
      "items": [
        "The East African Rift — the largest active continental rift, home to deep lakes such as Tanganyika and Malawi.",
        "The Baikal Rift in Siberia — cradle of Lake Baikal, the deepest and oldest freshwater lake on Earth.",
        "The Rhine Rift (Upper Rhine Graben) — a classic, well-studied rift running through France and Germany.",
        "The Rio Grande Rift — a rift system splitting the crust of the American Southwest.",
        "Mid-ocean ridges — vast undersea rifts where new ocean floor is created as plates pull apart beneath the waves."
      ]
    },
    {
      "type": "heading",
      "text": "Why Rift Valleys Matter"
    },
    {
      "type": "paragraph",
      "text": "Rift valleys are more than geological curiosities. Their deep, fault-bounded basins trap water to form some of the world’s longest and deepest lakes, which in turn host extraordinary concentrations of fish and other species found nowhere else. Their thin crust makes them prime ground for geothermal energy. And in East Africa, the rift’s sediments have preserved many of the most important fossils in the human story, which is why the region is often called the cradle of humankind."
    },
    {
      "type": "paragraph",
      "text": "In an overhead image, look for an elongated basin, steep margins, and aligned lakes. These are clues to investigate with a geological map; the shape alone does not establish that the crust is actively rifting."
    }
  ]
};
